/**
 * O cartão de mesa do Início e do Explorar: capa, marca sobreposta, nome e sistema, a mensagem de
 * status, o Sobre a mesa em duas linhas, a linha de como a mesa joga (frequência, visual, língua e
 * cobrança), as etiquetas numa linha com "+N" (procura jogadores, +18, senha, sua mesa, favorita e as
 * características) e o pé com o mestre e quantos estão na mesa.
 * Sem capa própria, a capa é gerada a partir do id da sala (arte nossa). A capa é uma faixa baixa, e o
 * que o mestre não preencheu (sistema, Sobre a mesa) não ocupa linha. O ⋮, o botão direito e o
 * segurar abrem o menu da mesa. Numa grade em linhas (`.rd-roomGrid--rows`, a lista do Explorar), a
 * capa some e a marca fica ao lado do texto.
 */
import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useLanguage, useT } from '../i18n/index.js';
import { parseTalemark, talemarkToPlainText, type RoomCard } from '../shared.js';
import { Avatar } from '../social/Avatar.js';
import { useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Icon, type IconName } from '../ui/icons.js';
import { toast } from '../ui/Toaster.js';
import { useLongPress } from '../ui/touch.js';
import { useExplore } from './state.js';
import { coverOf, cssUrl, generatedCover, roomImageSrc } from './roomImages.js';
import { languageName } from './roomDetails.js';

export { coverOf, generatedCover };

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => w.length > 2 || /^[A-ZÀ-Ý0-9]/.test(w));
  return ((words[0]?.[0] ?? name[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
}

/** "Sistema, complemento". */
export function systemOf(card: RoomCard): string {
  return [card.rpgSystem, card.rpgSystemComplement].filter(Boolean).join(', ');
}

export function statusLine(card: RoomCard): string {
  const system = systemOf(card);
  return card.statusMessage ? (system ? `${system} — ${card.statusMessage}` : card.statusMessage) : system;
}

export function roomLink(roomId: string): string {
  return `${window.location.origin}/?room=${encodeURIComponent(roomId)}`;
}

/**
 * O menu de contexto de um cartão de mesa (curtir, favoritar, visão geral, copiar link), o mesmo no
 * Explorar e no Início. `open` abre o menu num ponto; `view` é o menu aberto, ou nada.
 */
export function useRoomCardMenu(card: RoomCard, onOpen: (card: RoomCard) => void) {
  const t = useT();
  const toggleFavorite = useExplore((s) => s.toggleFavorite);
  const vote = useExplore((s) => s.vote);
  const [menu, setMenu] = useState<MenuAnchor | null>(null);
  const close = useCallback(() => setMenu(null), []);

  const entries: MenuEntry[] = [
    { kind: 'title', label: card.name },
    { label: t(card.myVote === 1 ? 'explore.menu.unlike' : 'explore.menu.like'), onSelect: () => void vote(card, 1) },
    { label: t(card.myVote === -1 ? 'explore.menu.undislike' : 'explore.menu.dislike'), onSelect: () => void vote(card, -1) },
    { kind: 'separator' },
    { label: t(card.isFavorite ? 'explore.menu.unfavorite' : 'explore.menu.favorite'), onSelect: () => void toggleFavorite(card) },
    { label: t('explore.menu.overview'), onSelect: () => onOpen(card) },
    {
      label: t('explore.menu.copyLink'),
      onSelect: () => {
        void navigator.clipboard?.writeText(roomLink(card.id)).then(() => toast(t('explore.menu.copied'), 'success'));
      },
    },
  ];

  const view = menu ? <ContextMenu anchor={menu} entries={entries} label={t('explore.card.menu', { name: card.name })} onClose={close} /> : null;
  return { open: setMenu, view };
}

/**
 * A marca da mesa: a imagem dela ou as iniciais sobre um fundo gerado. A imagem que não abre (o
 * arquivo foi para a lixeira, o endereço sumiu) volta às iniciais. `src` mostra outra imagem no lugar
 * da guardada (a prévia das configurações; null = nenhuma).
 */
export function RoomMark({ card, className, src }: { card: Pick<RoomCard, 'id' | 'name' | 'logoUrl'>; className: string; src?: string | null }) {
  const image = src !== undefined ? src : roomImageSrc(card.id, 'logo', card.logoUrl);
  const [failed, setFailed] = useState<string | null>(null);
  const shown = image && image !== failed ? image : null;
  return (
    <span className={className} style={shown ? { backgroundImage: cssUrl(shown) } : { background: generatedCover(`${card.id}:logo`) }} aria-hidden="true">
      {shown ? <img src={shown} alt="" hidden onError={() => setFailed(shown)} /> : initials(card.name)}
    </span>
  );
}

/** "N pessoas na mesa agora", com o ponto verde quando há alguém. */
export function OnlineLine({ card, className, children }: { card: RoomCard; className: string; children?: ReactNode }) {
  const t = useT();
  const text = card.onlineCount === 0 ? t('home.online.none') : card.onlineCount === 1 ? t('home.online.one') : t('home.online.many', { n: card.onlineCount });
  return (
    <span className={`${className}${card.onlineCount ? ' is-live' : ''}`}>
      <span className="rd-home__dot" aria-hidden="true" />
      {text}
      {children}
    </span>
  );
}

/** O nome de uma característica: a do catálogo traduzida, ou a tag livre como foi escrita. */
export function tagLabel(tag: string, t: (key: string) => string): string {
  const name = t(`roomTag.${tag}`);
  return name === `roomTag.${tag}` ? tag : name;
}

/** O "Sobre a mesa" em texto corrido, para o cartão; vazio quando o mestre não escreveu. */
export function aboutOf(about: string): string {
  return about.trim() ? talemarkToPlainText(parseTalemark(about)).replace(/\s+/g, ' ').trim() : '';
}

/** a largura reservada para o "+N" no fim da linha de etiquetas, e o espaço entre elas */
const MORE_WIDTH = 40;
const CHIP_GAP = 6;

/**
 * Quantas etiquetas cabem numa linha, guardando lugar para o "+N". Cada etiqueta é medida uma vez, com
 * todas à vista (`signature` muda quando elas mudam), e a conta se refaz quando a largura muda.
 */
function useChipFit(signature: string, total: number) {
  const ref = useRef<HTMLSpanElement>(null);
  const widths = useRef<number[]>([]);
  const [measured, setMeasured] = useState<string | null>(null);
  const [fit, setFit] = useState(total);
  const measuring = measured !== signature;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const compute = () => {
      let used = 0;
      let n = 0;
      for (const width of widths.current) {
        const next = used + (n ? CHIP_GAP : 0) + width;
        const rest = widths.current.length - n - 1;
        if (next + (rest > 0 ? CHIP_GAP + MORE_WIDTH : 0) > el.clientWidth) break;
        used = next;
        n += 1;
      }
      setFit(n);
    };
    // medir é ler o DOM: só aqui, com todas as etiquetas à vista
    if (measuring) {
      widths.current = [...el.children].map((child) => (child as HTMLElement).offsetWidth);
      compute();
      setMeasured(signature);
    }
    if (typeof ResizeObserver === 'undefined') return;
    const watch = new ResizeObserver(compute);
    watch.observe(el);
    return () => watch.disconnect();
  }, [measuring, signature]);
  return [ref, measuring ? total : fit] as const;
}

type Chip = { key: string; text: string; icon?: IconName; tone?: 'good' | 'warn' | 'fav' | 'mine' };

/**
 * Um cartão de mesa (docs/spec/explore-rooms.md, "Cartão"): a capa, a marca, o nome e o sistema, a
 * mensagem de status, o Sobre a mesa em duas linhas, a linha de como a mesa joga (frequência, visual,
 * língua e cobrança), as etiquetas numa linha (com "+N") e, no pé, o mestre e quantos estão na mesa.
 * `label` é o nome do botão (o que o clique faz) e o resto é a descrição dele; `status` troca o lado
 * direito do pé (o Início põe ali a última visita). `onOpen` abre a visão geral pelo menu.
 */
export function RoomTile({ card, label, onActivate, onOpen, status }: { card: RoomCard; label: string; onActivate: () => void; onOpen: (card: RoomCard) => void; status?: ReactNode }) {
  const t = useT();
  const ui = useLanguage((s) => s.language);
  const me = useApp((s) => s.user);
  const menu = useRoomCardMenu(card, onOpen);
  const press = useLongPress();
  const about = useId();
  const mine = card.ownerId === me?.id;
  const system = systemOf(card);
  const text = useMemo(() => aboutOf(card.about), [card.about]);

  const openMenu = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    menu.open({ x: e.clientX, y: e.clientY });
  };

  // como a mesa joga: sempre à vista, com a cobrança mesmo quando é gratuita
  const facts: { key: string; icon: IconName; text: string; title?: string; paid?: boolean }[] = [
    ...(card.frequency ? [{ key: 'frequency', icon: 'calendar' as const, text: t(`roomDetails.frequency.${card.frequency}`) }] : []),
    ...(card.visuals ? [{ key: 'visuals', icon: 'image' as const, text: t(`roomDetails.visuals.${card.visuals}`) }] : []),
    ...(card.language ? [{ key: 'language', icon: 'globe' as const, text: languageName(card.language, ui) }] : []),
    card.payToPlay ? { key: 'pay', icon: 'coin', text: t('roomDetails.paid'), title: card.price || undefined, paid: true } : { key: 'pay', icon: 'coin', text: t('roomDetails.free') },
  ];
  // as etiquetas: primeiro o que muda a decisão de entrar, depois as características
  const chips: Chip[] = [
    ...(card.haveVacancies ? [{ key: 'vacancies', text: t('explore.card.vacancies'), tone: 'good' as const }] : []),
    ...(card.restrictedUnder18 ? [{ key: 'adult', text: t('explore.card.adult'), tone: 'warn' as const }] : []),
    ...(card.hasPassword ? [{ key: 'password', text: t('explore.card.password'), icon: 'lock' as const }] : []),
    ...(mine ? [{ key: 'mine', text: t('explore.card.mine'), tone: 'mine' as const }] : []),
    ...(!mine && card.amIAuxGm ? [{ key: 'aux', text: t('explore.card.auxGm'), tone: 'mine' as const }] : []),
    ...(card.isFavorite ? [{ key: 'fav', text: t('home.group.favorites'), icon: 'star' as const, tone: 'fav' as const }] : []),
    ...card.tags.map((tag) => ({ key: `tag:${tag}`, text: tagLabel(tag, t) })),
  ];
  const [chipsRef, shown] = useChipFit(chips.map((chip) => chip.text).join('|'), chips.length);
  const rest = chips.slice(shown);

  return (
    <article className="rd-roomTile">
      <button type="button" className="rd-roomTile__hit" aria-label={label} aria-describedby={about} onClick={onActivate} {...press.bind(menu.open)}>
        <span className="rd-roomTile__cover" style={{ backgroundImage: coverOf(card) }} />
        <RoomMark card={card} className="rd-roomTile__mark" />
        <span className="rd-roomTile__body" id={about}>
          <span className="rd-roomTile__name">{card.name}</span>
          {/* o que o mestre não preencheu não ocupa linha; o pé fica alinhado embaixo de qualquer jeito */}
          {system ? <span className="rd-roomTile__system">{system}</span> : null}
          {card.statusMessage ? <span className="rd-roomTile__note">{card.statusMessage}</span> : null}
          {text ? <span className="rd-roomTile__about">{text}</span> : null}
          <span className="rd-roomTile__facts">
            {facts.map((fact) => (
              <span key={fact.key} className={`rd-roomTile__fact rd-roomTile__fact--${fact.key}${fact.paid ? ' is-paid' : ''}`} title={fact.title ?? fact.text}>
                <Icon name={fact.icon} size={14} />
                <span className="rd-roomTile__factText">{fact.text}</span>
              </span>
            ))}
          </span>
          <span className="rd-roomTile__chips" ref={chipsRef}>
            {chips.slice(0, shown).map((chip) => (
              <span key={chip.key} className={`rd-roomTile__chip${chip.tone ? ` is-${chip.tone}` : ''}`}>
                {chip.icon ? <Icon name={chip.icon} size={12} filled={chip.icon === 'star'} /> : null}
                {chip.text}
              </span>
            ))}
            {rest.length ? (
              <span className="rd-roomTile__chip is-more" title={rest.map((chip) => chip.text).join(', ')}>
                +{rest.length}
                <span className="rd-sr">: {rest.map((chip) => chip.text).join(', ')}</span>
              </span>
            ) : null}
          </span>
          <span className="rd-roomTile__foot">
            {card.ownerName ? (
              <span className="rd-roomTile__gm">
                <Avatar name={card.ownerName} color={card.ownerColor || '#5a5f6a'} size={24} userId={card.ownerId} avatarVersion={card.ownerAvatarVersion} />
                <span className="rd-roomTile__gmName">
                  {t('explore.card.gm')} <b>{card.ownerName}</b>
                </span>
                {card.ownerOnline ? <span className="rd-roomTile__gmHere">{t('explore.card.gmHere')}</span> : null}
              </span>
            ) : null}
            {status ?? <PlayingLine card={card} />}
          </span>
        </span>
      </button>
      <button type="button" className="rd-roomTile__more" aria-label={t('explore.card.menu', { name: card.name })} onClick={openMenu}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {menu.view}
    </article>
  );
}

/** "4 na mesa", com o ponto verde, ou "ninguém agora": o lado direito do pé do cartão. */
function PlayingLine({ card }: { card: RoomCard }) {
  const t = useT();
  if (!card.onlineCount) return <span className="rd-roomTile__playing">{t('explore.card.nobody')}</span>;
  return (
    <span className="rd-roomTile__playing is-live">
      <span className="rd-home__dot" aria-hidden="true" />
      {t(card.onlineCount === 1 ? 'explore.card.playing.one' : 'explore.card.playing.many', { n: card.onlineCount })}
    </span>
  );
}

/**
 * A lista vazia, com o que fazer em seguida em `children` (botões): o estado vazio do padrão
 * (ui/EmptyState.tsx). `wide` ocupa a largura da grade inteira.
 */
export function EmptyNote({ icon, title, text, wide = false, children }: { icon: IconName; title: string; text: string; wide?: boolean; children?: ReactNode }) {
  return (
    <EmptyState icon={icon} title={title} text={text} className={`rd-emptyNote${wide ? ' rd-emptyNote--wide' : ''}`}>
      {children}
    </EmptyState>
  );
}
